/**
 * Markdown as a view of a section's ProseMirror document (spec §8.19, M16). Nothing here is stored:
 * the Loro `pm` container stays the source of truth (Q36).
 *
 * Verified against prosemirror-markdown 1.13.7 and markdown-it 14.3: `MarkdownParser(schema, tokenizer,
 * tokens)` and `MarkdownSerializer(nodes, marks)`; markdown-it names strikethrough tokens `s_open`/
 * `s_close` (spec key `s`) and enables it only when asked in the `commonmark` preset.
 */
import MarkdownIt from "markdown-it";
import { defaultMarkdownParser, defaultMarkdownSerializer, MarkdownParser, MarkdownSerializer } from "prosemirror-markdown";
import type { Node } from "prosemirror-model";
import { pmSchema } from "./pmSchema";

/**
 * The tokenizer knows only what the schema can hold. Rules for constructs the schema lacks — images,
 * fenced and indented code, raw HTML — are disabled, so such markdown stays in the section as literal
 * text instead of being dropped (tables are already off in the commonmark preset).
 */
const tokenizer = MarkdownIt("commonmark", { html: false, linkify: false }).enable("strikethrough").disable(["image", "fence", "code", "html_block", "html_inline"]);

/** A copy of `record` without the schema's missing node types. */
function without<T>(record: Record<string, T>, ...keys: string[]): Record<string, T> {
  return Object.fromEntries(Object.entries(record).filter(([k]) => !keys.includes(k)));
}

export const kssMarkdownParser = new MarkdownParser(pmSchema, tokenizer, { ...without(defaultMarkdownParser.tokens, "code_block", "fence", "image"), s: { mark: "strike" } });

const nodes = without(defaultMarkdownSerializer.nodes, "code_block", "image");

export const kssMarkdownSerializer = new MarkdownSerializer(
  {
    ...nodes,
    bullet_list(state, node) {
      state.renderList(node, "  ", () => "- ");
    },
    list_item(state, node) {
      node.forEach((child, _offset, i) => {
        // CommonMark: an ordered list starting at n ≠ 1 cannot interrupt a paragraph, so in a tight item
        // "3. nested" would read as text; a blank line keeps it a list (the schema has no tightness)
        if (i > 0 && child.type.name === "ordered_list" && child.attrs.order !== 1) (state as unknown as { flushClose(size: number): void }).flushClose(2);
        state.render(child, node, i);
      });
    },
  },
  { ...defaultMarkdownSerializer.marks, strike: { open: "~~", close: "~~", mixable: true, expelEnclosingWhitespace: true } },
);

export function toMarkdown(doc: Node): string {
  return kssMarkdownSerializer.serialize(doc, { tightLists: true });
}

export function fromMarkdown(text: string): Node {
  return kssMarkdownParser.parse(text);
}

/** http(s) and mailto only: other schemes (javascript:, data:) never become links (spec §8.19). */
export function safeHref(href: unknown): string | null {
  if (typeof href !== "string") return null;
  try {
    const u = new URL(href, "https://invalid.invalid/");
    return ["http:", "https:", "mailto:"].includes(u.protocol) && /^(https?:|mailto:)/i.test(href.trim()) ? href.trim() : null;
  } catch {
    return null;
  }
}
